import { Avatar, AvatarGroup, Box, IconButton, Skeleton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { useAuth } from '../../core/contexts/AuthContext';
import { ProjectMemberDialog } from './dialogs/ProjectMemberDialog';
import type { ProjectMember } from '../../core/types/data-models';

interface ProjectMembersHeaderProps {
  projectId: number;
}

/**
 * Avatar stack for the project header: everyone with access to this project,
 * plus a button to invite someone new. Replaces the old "Shared With" card
 * and "People with access" panel — clicking an avatar opens the same dialog
 * used to invite, pre-filled for that person.
 *
 * Hidden entirely without `project:manage_members`, matching the panel it
 * replaces — a REVIEWER or VIEWER invited to a project cannot re-share it.
 */
export function ProjectMembersHeader({ projectId }: ProjectMembersHeaderProps) {
  const { canManageMembers } = usePermissions();
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);

  const membersApi = resources.projectMembersFor(projectId);
  const membersQuery = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => membersApi.list({ pageSize: 200 }),
    enabled: canManageMembers,
  });

  if (!canManageMembers) return null;

  if (membersQuery.isLoading) {
    return <Skeleton variant="circular" width={30} height={30} />;
  }

  const active = (membersQuery.data?.data ?? []).filter((m) => m.status !== 'REVOKED');
  const ownLabel = user?.name ?? user?.email ?? 'You';

  const openInvite = () => {
    setEditingMember(null);
    setDialogOpen(true);
  };

  const openEdit = (m: ProjectMember) => {
    setEditingMember(m);
    setDialogOpen(true);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Always shown first so the control reads as "project members" rather than a bare "+" button. */}
        <Tooltip title={`${ownLabel} (you)`}>
          <Avatar sx={{ width: 30, height: 30, fontSize: 12 }}>{ownLabel[0]?.toUpperCase()}</Avatar>
        </Tooltip>
        {active.length > 0 && (
          <AvatarGroup
            max={6}
            sx={{ ml: -0.5, '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 12, cursor: 'pointer' } }}
          >
            {active.map((m) => {
              const label = m.name ?? m.invitedName ?? m.email;
              return (
                <Tooltip key={m.id} title={m.status === 'PENDING' ? `${label} (pending)` : label}>
                  <Avatar
                    src={m.avatarUrl ?? undefined}
                    onClick={() => openEdit(m)}
                    sx={{ opacity: m.status === 'PENDING' ? 0.6 : 1 }}
                  >
                    {label[0]?.toUpperCase()}
                  </Avatar>
                </Tooltip>
              );
            })}
          </AvatarGroup>
        )}
        <Tooltip title="Invite someone">
          <IconButton
            size="small"
            onClick={openInvite}
            sx={{
              width: 30,
              height: 30,
              ml: -0.5,
              border: '1.5px dashed',
              borderColor: 'divider',
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <ProjectMemberDialog
        projectId={projectId}
        open={dialogOpen}
        member={editingMember}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
